import { Component, EventEmitter, OnInit, Output } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { combineLatest, map, Observable, switchMap, take } from 'rxjs'
import { NavService } from './../../../nav.service'
import { AppState } from './../../../core/store/index'
import { isDefinedString } from '../../../credit/utils/credit.utils'
import { Client, ClientYear } from './../../../core/model/client.model'
import { Study } from './../../../core/model/study.model'
import { TierPath } from '../../tier.model'
import {
  MI_CONTEXT_PATHS,
  GSE_CONTEXT_PATHS,
  CreditModuleContext,
} from './../../../credit/model/credit-routes.model'
import { ProgramsYearsFilter } from '../credit-tier.model'
import {
  selectGSECreditClients,
  selectMICreditClients,
} from '../../../credit/store/credit.selectors'
import { selectRouterStateUrl } from '../../../core/store/router.selectors'

import {
  selectCurrentClientID,
  selectCurrentClientStudies,
  selectCurrentClientYears,
} from '../../../core/store/broker/broker.selectors'
import { selectGroupFilters } from './../../../credit/store/group/credit-group.selectors'
import { selectGseGroupFilters } from './../../../credit/store/gse-group/gse-group.selectors'
import { selectCreditModuleContext } from './../../../credit/store/credit.selectors'
import { clearCheckedPrograms } from './../../../core/store/broker/broker.actions'
import { CreditStructureGroupFilterActions } from './../../../credit/store/group/credit-group.actions'
import { GseStructureGroupFilterActions } from './../../../credit/store/gse-group/gse-group.actions'
import * as fromCreditMetricActions from '../../../credit/store/metrics/credit-metrics.actions'
import * as fromCreditMetric from '../../../credit/store/metrics/credit-metrics.selectors'

@Component({
  selector: 'app-credit-tier-bar-multiselect-container',
  templateUrl: './credit-tier-bar-multiselect.container.html',
})
export class CreditTierBarMultiselectContainerComponent implements OnInit {
  clients$: Observable<readonly Client[]>
  years$: Observable<readonly ClientYear[]>
  programs$: Observable<readonly Study[]>
  selectedClientId$: Observable<string | null>
  filters$: Observable<ProgramsYearsFilter>
  yearsDisabled$: Observable<boolean>
  programsDisabled$: Observable<boolean>

  @Output() tierChange = new EventEmitter()

  constructor(
    private nav: NavService,
    private store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    this.clients$ = this.store.pipe(
      select(selectRouterStateUrl),
      switchMap((url) =>
        url?.includes('/credit/gse')
          ? this.store.select(selectGSECreditClients)
          : this.store.select(selectMICreditClients),
      ),
    )
    this.years$ = this.store.pipe(
      select(selectCurrentClientYears),
      map((years) => years.slice().reverse()),
    )
    this.programs$ = this.store.pipe(
      select(selectCurrentClientStudies),
      map((programs) =>
        programs.filter((program) => isDefinedString(program.credit_sage_portfolio_id)),
      ),
    )
    this.selectedClientId$ = this.store.pipe(select(selectCurrentClientID))
    this.filters$ = this.store.pipe(
      select(selectCreditModuleContext),
      switchMap((context) =>
        context === CreditModuleContext.GseGroup || context === CreditModuleContext.GseCompare
          ? this.store.select(selectGseGroupFilters)
          : this.store.select(selectGroupFilters),
      ),
    )
    this.yearsDisabled$ = combineLatest([
      this.store.select(selectCreditModuleContext),
      this.store.select(selectCurrentClientYears),
      this.store.select(fromCreditMetric.selectMetricConfiguration),
    ]).pipe(
      map(
        ([moduleContext, years, metricConfiguration]) =>
          (moduleContext === CreditModuleContext.Compare ||
            moduleContext === CreditModuleContext.GseCompare) &&
          (years.length < 1 || !metricConfiguration),
      ),
    )
    this.programsDisabled$ = combineLatest([
      this.store.select(selectCreditModuleContext),
      this.store.select(selectCurrentClientStudies),
      this.store.select(fromCreditMetric.selectMetricConfiguration),
    ]).pipe(
      map(
        ([moduleContext, programs, metricConfiguration]) =>
          (moduleContext === CreditModuleContext.Compare ||
            moduleContext === CreditModuleContext.GseCompare) &&
          (programs.length < 1 || !metricConfiguration),
      ),
    )
  }

  onTierChange(tierPath: TierPath): void {
    this.store.dispatch(clearCheckedPrograms())
    this.store.dispatch(CreditStructureGroupFilterActions.reset())
    this.store.dispatch(GseStructureGroupFilterActions.reset())
    combineLatest([
      this.store.pipe(select(selectCreditModuleContext)),
      this.store.pipe(select(selectRouterStateUrl)),
    ])
      .pipe(take(1))
      .subscribe(([context, url]) => {
        const isGse = url?.includes('/credit/gse') ?? false
        if (context === CreditModuleContext.Group) {
          this.nav.navigateWithTierPath(tierPath, ...MI_CONTEXT_PATHS.Group)
        }
        if (context === CreditModuleContext.GseGroup) {
          this.nav.navigateWithTierPath(tierPath, ...GSE_CONTEXT_PATHS.Group)
        }
        if (context === CreditModuleContext.Compare || context === CreditModuleContext.GseCompare) {
          this.nav.navigateWithTierPath(
            tierPath,
            ...(isGse ? GSE_CONTEXT_PATHS.Compare : MI_CONTEXT_PATHS.Compare),
          )
          const carrierId = Number(tierPath.client)
          if (carrierId) {
            this.store.dispatch(
              fromCreditMetricActions.GetMetricConfigurationNamesByCarrierId({
                carrierId,
                isGse,
              }),
            )
          }
          this.tierChange.emit() // on client change, close dialog
        }
      })
  }

  onUpdateFilters(filters: ProgramsYearsFilter): void {
    this.store.pipe(select(selectCreditModuleContext), take(1)).subscribe((context) => {
      if (context === CreditModuleContext.GseGroup || context === CreditModuleContext.GseCompare) {
        this.store.dispatch(GseStructureGroupFilterActions.update({ filters }))
      } else {
        this.store.dispatch(CreditStructureGroupFilterActions.update({ filters }))
      }
    })
  }
}
